import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import bindAll from 'lodash.bindall';

import {
    closeAiChat,
    addAiMessage,
    setAiLoading,
    clearAiMessages,
    setTokenBalance
} from '../reducers/ai-chat';
import AiChatComponent from '../components/ai-chat/ai-chat.jsx';
import {
    injectBlocksFromXML,
    replaceWorkspaceXML,
    getWorkspaceXML,
    clearWorkspace,
    undoLastAiAction,
    hasUndoSnapshot,
    extractXML,
    stripXML
} from '../lib/ai-block-injector';
import {buildScratchSystemPrompt} from '../lib/scratch-block-reference';

const apiUrl = process.env.AIPNT_API_URL;

const EDIT_KEYWORDS = [
    'change', 'edit', 'modify', 'update', 'replace',
    'fix', 'adjust', 'set', 'make it',
    'increase', 'decrease', 'remove', 'delete',
    'wrap', 'surround'
];

// Words that signal ADD intent even when edit keywords are present
const ADD_KEYWORDS = [
    'add', 'also', 'new', 'another', 'create',
    'plus', 'include', 'extra', 'more'
];

/**
 * Context-aware intent detection.
 * @param {string} text User input
 * @param {boolean} hasBlocks Whether workspace has blocks
 * @returns {boolean} true if edit mode
 */
const isEditIntent = (text, hasBlocks) => {
    if (!hasBlocks) return false;
    const lower = text.toLowerCase();
    // If user explicitly wants to ADD something, don't enter edit mode
    if (ADD_KEYWORDS.some(kw => lower.includes(kw))) return false;
    return EDIT_KEYWORDS.some(kw => lower.includes(kw));
};

class AiChat extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            inputValue: '',
            mode: 'add', // 'add' or 'edit'
            canUndo: false,
            isTranscribing: false
        };
        bindAll(this, [
            'handleClose',
            'handleInputChange',
            'handleSendMessage',
            'handleClearCanvas',
            'handleUndo',
            'handleSuggestionClick',
            'callBackendAPI',
            'fetchBalance',
            'handleTranscribe'
        ]);
    }

    componentDidUpdate (prevProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.fetchBalance();
        }
    }

    async fetchBalance () {
        try {
            const token = sessionStorage.getItem('aipnt_device_session');
            if (!token) return;
            const res = await fetch(`${apiUrl}/api/licensing/balance`, {
                headers: {Authorization: `Bearer ${token}`}
            });
            if (res.ok) {
                const data = await res.json();
                this.props.onSetTokenBalance(data.balanceTokens);
            }
        } catch (e) {
            // Ignore errors for fetching balance
        }
    }

    handleClose () {
        this.props.onCloseAiChat();
    }

    async handleTranscribe (audioBase64, mimeType) {
        this.setState({ isTranscribing: true });
        try {
            const token = sessionStorage.getItem('aipnt_device_session');
            if (!token) throw new Error('No session token');

            const res = await fetch(`${apiUrl}/api/ai/transcribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ audioBase64, mimeType, locale: this.props.locale })
            });

            if (!res.ok) {
                throw new Error('Transcription failed');
            }

            const data = await res.json();
            if (data.transcription) {
                this.setState(prevState => ({
                    inputValue: prevState.inputValue ? `${prevState.inputValue} ${data.transcription}` : data.transcription
                }));
            }
            if (data.balanceTokens !== undefined) {
                this.props.onSetTokenBalance(data.balanceTokens);
            }
        } catch (e) {
            console.error('Transcription error:', e);
        } finally {
            this.setState({ isTranscribing: false });
        }
    }

    handleInputChange (e) {
        this.setState({inputValue: e.target.value});
    }

    handleClearCanvas () {
        const success = clearWorkspace();
        if (success) {
            this.props.onAddMessage({
                role: 'ai',
                content: 'Canvas cleared!'
            });
            this.setState({canUndo: false});
        }
    }

    handleUndo () {
        const success = undoLastAiAction();
        if (success) {
            this.props.onAddMessage({
                role: 'ai',
                content: 'Reverted to previous state.'
            });
            this.setState({canUndo: false});
        }
    }

    handleSuggestionClick (text) {
        this.setState({inputValue: text}, () => {
            this.handleSendMessage();
        });
    }

    buildSystemPrompt (editMode, workspaceXML) {
        return buildScratchSystemPrompt({
            editMode, 
            workspaceXML,
            locale: this.props.locale
        });
    }

    /**
     * Build conversation history from Redux messages, filtering out
     * transient status messages and keeping only the last N turns.
     */
    _buildConversationHistory () {
        const MAX_HISTORY = 6; // 3 user + 3 AI turns
        const TRANSIENT = [
            'Generating blocks...', 'Editing blocks...',
            'Blocks added to your workspace!', 'Blocks updated!',
            'Canvas cleared!'
        ];

        return this.props.messages
            .filter(m => !TRANSIENT.includes(m.content))
            .filter(m => !m.content.startsWith('Error:'))
            .slice(-MAX_HISTORY)
            .map(m => ({
                role: m.role === 'user' ? 'user' : 'ai',
                content: m.content
            }));
    }

    async callBackendAPI (userText, editMode) {
        const workspaceXML = getWorkspaceXML(); // Always pass context if available
        const systemPrompt = this.buildSystemPrompt(editMode, workspaceXML);

        // Build messages: system prompt + conversation history + current user message
        const history = this._buildConversationHistory();
        const messages = [
            {role: 'system', content: systemPrompt},
            ...history,
            {role: 'user', content: userText}
        ];

        try {
            const token = sessionStorage.getItem('aipnt_device_session');
            if (!token) throw new Error('Device session not found. Please log in.');

            const response = await fetch(`${apiUrl}/api/ai/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({messages, locale: this.props.locale})
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || `API Error: ${response.statusText}`);
            }

            if (data.balanceTokens !== undefined) {
                this.props.onSetTokenBalance(data.balanceTokens);
            }

            return {
                response: data.response,
                audioBase64: data.audioBase64
            };
        } catch (e) {
            return {
                response: `Error: ${e.message}`
            };
        }
    }

    async handleSendMessage () {
        const text = this.state.inputValue.trim();
        if (!text || this.props.isLoading) return;

        this.setState({inputValue: ''});

        // Detect mode
        const currentXML = getWorkspaceXML();
        const hasBlocks = Boolean(currentXML);
        const editMode = isEditIntent(text, hasBlocks);

        this.setState({
            mode: editMode ? 'edit' : 'add'
        });

        // We keep isEditIntent to decide whether to replace or append,
        // but the backend AI handles natural language + XML.
        const modeLabel = 'Thinking...';

        this.props.onAddMessage({
            role: 'user', content: text
        });
        this.props.onAddMessage({
            role: 'ai', content: modeLabel
        });
        this.props.onSetLoading(true);

        const apiResult = await this.callBackendAPI(
            text, editMode
        );
        const aiResponse = apiResult.response;
        this.props.onSetLoading(false);

        if (apiResult.audioBase64) {
            try {
                const audio = new Audio(`data:audio/mp3;base64,${apiResult.audioBase64}`);
                audio.play().catch(e => console.error('Audio play error:', e));
            } catch (e) {
                console.error('Audio initialization error:', e);
            }
        }

        // Does the response contain blocks to inject?
        const responseHasBlocks = extractXML(aiResponse) !== null;

        if (responseHasBlocks) {
            let success = false;
            if (editMode) {
                success = await replaceWorkspaceXML(aiResponse, this.props.vm);
            } else {
                success = await injectBlocksFromXML(aiResponse, this.props.vm);
            }

            if (success) {
                let chatText = stripXML(aiResponse);
                if (!chatText) {
                    chatText = editMode ? 'Blocks updated!' : 'Blocks added to your workspace!';
                }
                this.props.onAddMessage({
                    role: 'ai', content: chatText
                });
                this.setState({canUndo: hasUndoSnapshot()});
            } else {
                this.props.onAddMessage({
                    role: 'ai', content: 'Oops! I tried to generate blocks, but they were invalid.'
                });
            }
        } else {
            // No XML, just a conversational response
            this.props.onAddMessage({
                role: 'ai', content: aiResponse
            });
        }
    }

    render () {
        return (
            <AiChatComponent
                canUndo={this.state.canUndo}
                inputValue={this.state.inputValue}
                isLoading={this.props.isLoading}
                isOpen={this.props.isOpen}
                messages={this.props.messages}
                mode={this.state.mode}
                tokenBalance={this.props.tokenBalance}
                onClearCanvas={this.handleClearCanvas}
                onClearMessages={this.props.onClearMessages}
                onClose={this.handleClose}
                onInputChange={this.handleInputChange}
                onSendMessage={this.handleSendMessage}
                onSuggestionClick={this.handleSuggestionClick}
                onUndo={this.handleUndo}
                onTranscribe={this.handleTranscribe}
                isTranscribing={this.state.isTranscribing}
            />
        );
    }
}

AiChat.propTypes = {
    isLoading: PropTypes.bool,
    isOpen: PropTypes.bool,
    locale: PropTypes.string,
    messages: PropTypes.array,
    tokenBalance: PropTypes.number,
    vm: PropTypes.object,
    onAddMessage: PropTypes.func,
    onCloseAiChat: PropTypes.func,
    onSetLoading: PropTypes.func,
    onSetTokenBalance: PropTypes.func
};

const mapStateToProps = state => ({
    isOpen: state.scratchGui.aiChat.isOpen,
    messages: state.scratchGui.aiChat.messages,
    isLoading: state.scratchGui.aiChat.isLoading,
    tokenBalance: state.scratchGui.aiChat.tokenBalance,
    locale: state.locales.locale,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onCloseAiChat: () => dispatch(closeAiChat()),
    onAddMessage: msg => dispatch(addAiMessage(msg)),
    onSetLoading: val => dispatch(setAiLoading(val)),
    onClearMessages: () => dispatch(clearAiMessages()),
    onSetTokenBalance: val => dispatch(setTokenBalance(val))
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AiChat);
